/**
 * Detect the device's rough rendering capability at boot.
 * Used by the adaptive controller to pre-pick a starting point on the
 * quality ladder so phones don't have to first stutter their way down.
 */

export type DeviceClass = 'high' | 'medium' | 'low';

export interface DeviceProfile {
    cls: DeviceClass;
    isMobile: boolean;
    cores: number;
    memGB: number | null;       // navigator.deviceMemory may be undefined
    dpr: number;
    gpu: string | null;         // WebGL renderer string if extractable
}

export function detectDevice(canvas?: HTMLCanvasElement): DeviceProfile {
    const nav = navigator as unknown as { deviceMemory?: number; userAgentData?: { mobile?: boolean } };
    const cores = navigator.hardwareConcurrency ?? 4;
    const memGB = nav.deviceMemory ?? null;     // 0.25 / 0.5 / 1 / 2 / 4 / 8 ; some browsers omit
    const dpr = window.devicePixelRatio ?? 1;
    const isMobile = nav.userAgentData?.mobile ?? /Mobi|Android|iPhone|iPad/.test(navigator.userAgent);

    // GPU name: standard WEBGL_debug_renderer_info on a throwaway context.
    let gpu: string | null = null;
    try {
        const c = canvas ?? document.createElement('canvas');
        const gl = c.getContext('webgl2') as WebGL2RenderingContext | null;
        if (gl) {
            const dbg = gl.getExtension('WEBGL_debug_renderer_info');
            if (dbg) gpu = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string;
        }
    } catch { /* ignore */ }

    // Class heuristic. Conservative — we'd rather start "medium" on an unknown
    // box than start "high" and force the user to wait for the stutter loop.
    let cls: DeviceClass;
    if (isMobile || (memGB !== null && memGB < 4)) {
        cls = 'low';
    } else if ((memGB !== null && memGB < 8) || cores < 8) {
        cls = 'medium';
    } else {
        cls = 'high';
    }
    return { cls, isMobile, cores, memGB, dpr, gpu };
}
